<template>
  <div id="header_right_section" class="flex items-center justify-center gap-4">
    <span
      class="text-white font-semibold text-base leading-[17px] flex items-center justify-center gap-[6px]"
      v-if="isTaskCompleted"
    >
      <img
        class="size-5"
        :src="imagePath.dark.selected.checkIconRoundedWhite"
        alt="check_icon"
      />
      Marked as done
    </span>

    <template v-if="isTaskInProgress">
      <div
        v-if="shouldShowWhatToTalk"
        class="flex flex-row items-center justify-center gap-[6px] cursor-pointer banner_hover_action"
        @click="openWhatToTalkModal"
      >
        <img
          class="size-4"
          :src="callImagePath.whatToTalk"
          alt="what_to_talk"
        />
        <span class="text-white">What to talk</span>
      </div>

      <a-button
        v-if="!isLastTask"
        class="secondary_medium secondary_medium_dark"
        @click="handleSkipTask"
      >
        <img
          class="size-4"
          :src="imagePath.dark.action.skipIcon"
          alt="skip_icon"
        />
        Skip
      </a-button>

      <a-button
        class="primary_medium primary_medium_dark"
        @click="handleCompleteTask"
      >
        <img
          class="size-4"
          :src="imagePath.dark.selected.checkIconRoundedBlack"
          alt="complete_icon"
        />
        Mark as done
      </a-button>
    </template>

    <template v-else>
      <a-button
        @click="actionButtonConfig.mainAction"
        :class="actionButtonConfig.buttonClass"
      >
        <img
          :class="actionButtonConfig.iconClass"
          :src="actionButtonConfig.icon"
          :alt="actionButtonConfig.iconAlt"
        />
        {{ actionButtonConfig.text }}
        <a-dropdown placement="bottomRight" @click.stop>
          <img
            :src="actionButtonConfig.dropdownIcon"
            alt="dropdown_arrow"
            class="size-4 dropdown-white"
            @click.stop
          />
          <template #overlay>
            <a-menu>
              <a-menu-item
                key="mark-todo"
                @click="actionButtonConfig.markAsTodoAction"
              >
                <div class="flex items-center gap-2">Mark as To Do</div>
              </a-menu-item>
              <a-menu-item
                key="follow-up-task"
                @click="actionButtonConfig.createFollowUpTaskAction"
              >
                <div class="flex items-center gap-2">Create Follow Up Task</div>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </a-button>
    </template>

    <div class="flex flex-col gap-1">
      <span class="text-white/60 font-semibold text-sm leading-[17px]"
        >Progress</span
      >
      <span class="text-white font-bold text-base leading-[19px]">
        {{ currentAutoplayTask?.queue_stats?.current_count }} of
        {{ currentAutoplayTask?.queue_stats?.total_count }}
      </span>
    </div>
  </div>
  <FollowUpTaskModal
    v-model:visible="followUpTaskModalVisible"
    :parent-task="currentAutoplayTask?.task"
    @task-created="onFollowUpTaskCreated"
  />
</template>

<script setup>
import { computed, inject, watch, ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import {
  taskActionTypes,
  taskErrorTypes,
  taskStatusTypes,
} from "@/src/constants/taskConstants.js";
import imagePath from "@/src/paths/imagePath.js";
import callImagePath from "@/src/paths/callImagePath.js";
import DashboardService from "@/src/api/DashboardService.js";
import { marketResearchStatus } from "@/src/constants/researchStatusConstants";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import eventBus from "@/src/utils/eventBus.js";
import FollowUpTaskModal from "@/src/views/tasks/FollowUpTaskModal.vue";
import { triggerEvents } from "@/src/constants/formConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { useFormTrigger } from "@/src/composables/forms/useFormTrigger.js";

const router = useRouter();
const { executeBeforeTrigger, executeAfterTrigger } = useFormTrigger();
const message = inject("showMessage");
const store = inject("store");
const bannerStatuses = inject("bannerStatuses");
const whatToTalk = inject("whatToTalk");
const taskDetails = ref(null);
const followUpTaskModalVisible = ref(false);

const currentAutoplayTask = computed(
  () => store.getters["tasks/currentAutoPlayTask"],
);

const taskLinkedEntityDetails = computed(
  () => store.getters["tasks/taskLinkedEntityDetails"],
);

// Helper to get task status - handles both string and CRM object format { value: "..." }
const getTaskStatus = () => {
  const status = currentAutoplayTask.value?.task?.status;
  if (!status) return "";
  if (typeof status === "object" && status.value !== undefined) {
    return status.value;
  }
  return status;
};

const isTaskInProgress = computed(() => {
  if (currentAutoplayTask.value) {
    const status = getTaskStatus();
    return (
      status !== taskStatusTypes.Done &&
      status !== taskStatusTypes.COMPLETE &&
      status !== taskStatusTypes.DONE
    );
  }
});

const isTaskCompleted = computed(() => {
  if (currentAutoplayTask.value?.task) {
    const status = getTaskStatus();
    return (
      status === taskStatusTypes.COMPLETE ||
      status === taskStatusTypes.DONE ||
      status === taskStatusTypes.Done
    );
  }
  return false;
});

const isLastTask = computed(() => {
  try {
    const stats = currentAutoplayTask.value?.queue_stats;
    return stats?.current_count === stats?.total_count;
  } catch (error) {
    console.error("Error checking if last task:", error);
    return false;
  }
});

const actionButtonConfig = computed(() => {
  if (isLastTask.value) {
    return {
      mainAction: handleBackToTasks,
      buttonClass: "primary_medium primary_medium_dark",
      icon: imagePath.dark.action.backToTasksIcon,
      iconClass: "size-5",
      iconAlt: "back_to_tasks_icon",
      text: "Complete the Task Play",
      dropdownIcon: imagePath.dark.arrows.bottomArrowBlackIcon,
      markAsTodoAction: handleMarkAsTodoAndComplete,
      createFollowUpTaskAction: handleCreateFollowUpTask,
    };
  } else {
    return {
      mainAction: handleSkipTask,
      buttonClass: "secondary_medium secondary_medium_dark",
      icon: imagePath.dark.arrows.rightArrowDefault,
      iconClass: "size-4",
      iconAlt: "next_icon",
      text: "Next",
      dropdownIcon: imagePath.dark.arrows.bottomArrowWhite,
      markAsTodoAction: handleMarkAsTodo,
      createFollowUpTaskAction: handleCreateFollowUpTask,
    };
  }
});

const isStatusCompleted = (status) => {
  return (
    status === taskStatusTypes.COMPLETE ||
    status === taskStatusTypes.DONE ||
    status === taskStatusTypes.Done
  );
};

const fetchTaskDetails = async () => {
  if (!currentAutoplayTask.value?.task?.id) return;

  try {
    const response = await DashboardService.getTask(
      currentAutoplayTask.value?.task?.id,
    );
    // Check if response exists and has the expected structure
    // The API might return the task directly or wrapped in a success response
    if (response) {
      let taskData = null;

      // Handle wrapped response format
      if (response.success && response.task) {
        taskData = response.task;
      }
      // Handle direct task object format (has id and other task properties)
      else if (response.id && response.status !== undefined) {
        taskData = response;
      }

      if (taskData) {
        taskDetails.value = taskData;

        // Update the Vuex store with the complete task data
        const updatedTask = {
          ...currentAutoplayTask.value,
          task: {
            ...currentAutoplayTask.value.task,
            ...taskData,
          },
        };
        store.commit("tasks/SET_CURRENT_AUTOPLAY_TASK", updatedTask);
      }
    }
  } catch (error) {
    console.error("Error fetching task details:", error);
  }
};

const shouldShowWhatToTalk = computed(() => {
  if (taskDetails.value?.action_type === taskActionTypes.PHONE_CALL) {
    const removeWhatToTalkEnabled =
      localStorage.getItem("sequences_remove_what_to_talk") === "true";
    return !removeWhatToTalkEnabled;
  }
  return false;
});

const handleTaskNotFound = async (moveToNextTask = false) => {
  if (moveToNextTask) {
    message.error("Task was deleted. Skipping to the next task.");
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return handleSkipTask();
  }

  message.error("Task was deleted.");
  return null;
};

const handleQueueEmpty = async (showMessage = false) => {
  if (!currentAutoplayTask.value) return;

  if (showMessage) {
    message.error("No more tasks to play.");
  }

  await new Promise((resolve) => setTimeout(resolve, 2000));
  await store.dispatch("tasks/resetAutoPlayState");
  await store.dispatch("dynamicBanner/updateShowBanner", false);
};

const handleSkipTask = async () => {
  try {
    const response = await store.dispatch("tasks/loadNextAutoPlayTask");
    const nextTaskId = response?.task?.id;
    if (nextTaskId) {
      await router.push(`/tasks/${nextTaskId}`);
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message;

    switch (errorMessage) {
      case taskErrorTypes.TASK_NOT_FOUND:
        return handleTaskNotFound(true);
      case taskErrorTypes.QUEUE_EMPTY:
        return handleQueueEmpty(true);
      default:
        message.error("Failed to skip task");
        console.error("Error skipping task:", error);
        return null;
    }
  }
};

const handleCompleteTask = async () => {
  try {
    const taskId = currentAutoplayTask.value?.task?.id;
    if (!taskId) return;

    const { proceed } = await executeBeforeTrigger(
      entityTypes.CRM_TASKS,
      taskId,
      triggerEvents.TASK_COMPLETED,
    );
    if (!proceed) return;

    const completeResult = await store.dispatch("tasks/completeTask");
    if (completeResult?.success) {
      message.success("Task completed successfully");
      await executeAfterTrigger(taskId);
      eventBus.emit("task-status-changed", {
        taskId: taskId,
        status: "Done",
      });
    } else {
      message.error("Failed to complete task");
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message;

    switch (errorMessage) {
      case taskErrorTypes.TASK_NOT_FOUND:
        await handleTaskNotFound(false);
        if (isLastTask.value) {
          await handleQueueEmpty(true);
        }
        return null;

      default:
        message.error("Failed to complete task");
        console.error("Error completing task:", error);
        return null;
    }
  }
};

const handleBackToTasks = async () => {
  try {
    await Promise.all([
      store.dispatch("tasks/resetAutoPlayState"),
      store.dispatch("dynamicBanner/updateShowBanner", false),
    ]);
    await router.push("/tasks");
  } catch (error) {
    message.error("Failed to go back to tasks");
    console.error("Error going back to tasks:", error);
  }
};

const handleMarkAsTodo = async () => {
  if (!currentAutoplayTask.value?.task?.id) {
    message.error("Task not found");
    return;
  }

  const taskId = currentAutoplayTask.value.task.id;

  try {
    const { proceed } = await executeBeforeTrigger(
      entityTypes.CRM_TASKS,
      taskId,
      triggerEvents.ENTITY_UPDATED,
      ["status"],
    );
    if (!proceed) return;

    const response = await DashboardService.updateTaskStatus(
      taskId,
      {
        status: "Todo",
        completed_date: null,
      },
    );

    if (response && (response.success || response.id)) {
      const taskData = response.task || response;
      message.success("Task is marked as To Do successfully!");
      await executeAfterTrigger(taskId);

      const updatedTask = {
        ...currentAutoplayTask.value,
        task: {
          ...currentAutoplayTask.value.task,
          ...taskData,
        },
      };
      store.commit("tasks/SET_CURRENT_AUTOPLAY_TASK", updatedTask);

      eventBus.emit("task-status-changed", {
        taskId: taskId,
        status: "Todo",
      });

      await fetchTaskDetails();
    } else {
      message.error("Failed to mark task as To Do");
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message;

    if (errorMessage === taskErrorTypes.TASK_NOT_FOUND) {
      return handleTaskNotFound(true);
    }

    message.error("Failed to mark task as To Do");
    console.error("Error marking task as To Do:", error);
  }
};

const handleMarkAsTodoAndComplete = async () => {
  if (!currentAutoplayTask.value?.task?.id) {
    message.error("Task not found");
    return;
  }

  const taskId = currentAutoplayTask.value.task.id;

  try {
    const { proceed } = await executeBeforeTrigger(
      entityTypes.CRM_TASKS,
      taskId,
      triggerEvents.ENTITY_UPDATED,
      ["status"],
    );
    if (!proceed) return;

    const response = await DashboardService.updateTaskStatus(
      taskId,
      {
        status: "Todo",
        completed_date: null,
      },
    );

    if (response && (response.success || response.id)) {
      const taskData = response.task || response;
      message.success("Task is marked as To Do successfully!");
      await executeAfterTrigger(taskId);

      const updatedTask = {
        ...currentAutoplayTask.value,
        task: {
          ...currentAutoplayTask.value.task,
          ...taskData,
        },
      };
      store.commit("tasks/SET_CURRENT_AUTOPLAY_TASK", updatedTask);

      eventBus.emit("task-status-changed", {
        taskId: taskId,
        status: "Todo",
      });

      await fetchTaskDetails();
    } else {
      message.error("Failed to mark task as To Do");
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message;

    if (errorMessage === taskErrorTypes.TASK_NOT_FOUND) {
      message.error("Task was deleted.");
      await handleBackToTasks();
      return;
    }

    message.error("Failed to mark task as To Do");
    console.error("Error marking task as To Do:", error);
  }
};

const openWhatToTalkModal = async () => {
  whatToTalk.open();
  whatToTalk.loading.value = true;

  if (!currentAutoplayTask.value?.task?.id) {
    message.error("Task ID not found");
    whatToTalk.loading.value = false;
    whatToTalk.showModal.value = false;
    return;
  }

  try {
    const response = await DashboardService.callPrepByTask(
      currentAutoplayTask.value?.task?.id,
    );

    if (response?.success && response.file_content) {
      whatToTalk.loading.value = false;
      let markdownContent = response.file_content
        .replace(/```markdown\n/, "")
        .replace(/```/, "");
      whatToTalk.notes.value = markdownContent;
    } else {
      whatToTalk.loading.value = false;
      whatToTalk.showModal.value = false;

      const statusMessages = {
        [marketResearchStatus.IN_PROGRESS]:
          "The call preparation summary is currently being generated. Please try again in 2 minutes.",
        [marketResearchStatus.TODO]:
          "The call preparation summary is currently being generated. Please try again in 2 minutes.",
        [marketResearchStatus.TIMED_OUT]:
          "Call preparation data generation timed out.",
        [marketResearchStatus.INSUFFICIENT_INFORMATION]:
          "Insufficient information to generate call preparation data.",
        [marketResearchStatus.INSUFFICIENT_INFORMATION_COMPANY_WEBSITE]:
          "Company website information was missing to generate call preparation data.",
        [marketResearchStatus.INSUFFICIENT_INFORMATION_ASSOCIATED_COMPANY]:
          "Associated company information was missing to generate call preparation data.",
        [marketResearchStatus.INSUFFICIENT_INFORMATION_LEAD_NAME]:
          "Lead name information was missing to generate call preparation data.",
        [marketResearchStatus.INSUFFICIENT_INFORMATION_CONTACT_NAME]:
          "Contact name information was missing to generate call preparation data.",
        [marketResearchStatus.INSUFFICIENT_CREDITS]:
          "Our AI Actions are over!! Please upgrade your plan to continue using SuperAGI",
      };

      const errorMessage =
        statusMessages[response?.call_prep_status] ||
        "An error occurred while preparing call preparation data.";

      if (
        response?.call_prep_status === marketResearchStatus.IN_PROGRESS ||
        response?.call_prep_status === marketResearchStatus.TODO
      ) {
        message.info(errorMessage);
      } else {
        message.error(errorMessage);
      }
    }
  } catch (error) {
    whatToTalk.loading.value = false;
    whatToTalk.showModal.value = false;

    message.error(
      error?.response?.data?.error ||
        "An error occurred while preparing call preparation data.",
    );
  }
};

const checkTaskStatusMismatch = async () => {
  if (!currentAutoplayTask.value?.task?.id || !taskDetails.value) return;

  const currentTaskStatus = currentAutoplayTask.value?.task?.status;
  const fetchedTaskStatus = taskDetails.value?.status;

  const isActuallyDone = isStatusCompleted(fetchedTaskStatus);
  const isCurrentlyDone = isStatusCompleted(currentTaskStatus);

  if (isActuallyDone && !isCurrentlyDone) {
    try {
      await store.dispatch("tasks/completeTask");
    } catch (error) {
      console.error("Error syncing task status:", error);
    }
  }
};

watch(
  currentAutoplayTask,
  async () => {
    try {
      await store.dispatch(
        "dynamicBanner/updateBannerStatus",
        isTaskInProgress.value
          ? bannerStatuses.IN_PROGRESS
          : bannerStatuses.COMPLETE,
      );
    } catch (error) {
      console.error("Error updating banner status:", error);
      message.error("Failed to update task status");
    }
  },
  { immediate: true },
);

const handleTaskStatusChanged = (event) => {
  const currentTaskId = parseInt(currentAutoplayTask.value?.task?.id);
  const eventTaskId = parseInt(event.taskId);

  if (currentTaskId && eventTaskId && currentTaskId === eventTaskId) {
    if (event.status) {
      const updatedTask = {
        ...currentAutoplayTask.value,
        task: {
          ...currentAutoplayTask.value.task,
          status: event.status,
        },
      };

      store.commit("tasks/SET_CURRENT_AUTOPLAY_TASK", updatedTask);
      fetchTaskDetails();
    }
  }
};

watch(
  () => currentAutoplayTask.value?.task?.id,
  async (newTaskId) => {
    if (newTaskId) {
      await fetchTaskDetails();
      await checkTaskStatusMismatch();
    } else {
      taskDetails.value = null;
    }
  },
  { immediate: true },
);

const handleCreateFollowUpTask = () => {
  followUpTaskModalVisible.value = true;
};
const onFollowUpTaskCreated = () => {
  followUpTaskModalVisible.value = false;

  eventBus.emit("refresh-tasks");
};

onMounted(async () => {
  if (currentAutoplayTask.value?.task?.id) {
    await fetchTaskDetails();
    await checkTaskStatusMismatch();
  }

  eventBus.on("task-status-changed", handleTaskStatusChanged);
});

onBeforeUnmount(() => {
  eventBus.off("task-status-changed", handleTaskStatusChanged);
});
</script>

<style scoped>
.dropdown-white {
  margin-right: -10px;
  margin-left: 10px;
}
</style>
